// The pictures of files in Locked. Opening every one would hold the Locked listing up, so the API
// leaves them out of it, and they are asked for here once the listing is on screen, for the files
// shown and no others, a few dozen at a time. Until a file's answer comes back its tile shimmers,
// as one still being secured does; one the API has no picture for keeps its kind glyph.
//
// Each picture comes back whole, as a "data:" url, and is kept here rather than in the Apollo
// cache, so resealing Locked drops every one of them along with whatever asks were still out.
import {useEffect, useRef, useState} from 'react'
import {useApolloClient} from '@apollo/client/react'

import {LOCKED_THUMBNAILS} from '../../graphql/queries/locked'
import {fileKind, isSecured} from '../fileTools'

// The most the API opens for one ask.
const MOST_AT_ONCE = 48

// The kinds the worker makes a picture of.
const PICTURED = ['image', 'video', 'pdf']

const NOTHING = {pictures: new Map(), settled: new Set()}

const wantsPicture = file => Boolean(file.thumbnail) && !file.thumbnail.url && isSecured(file) && PICTURED.includes(fileKind(file.filename))

const batchesOf = ids => Array.from({length: Math.ceil(ids.length / MOST_AT_ONCE)}, (batch, index) => (
  ids.slice(index * MOST_AT_ONCE, (index + 1) * MOST_AT_ONCE)
))

/**
 * "open" is whether Locked is open and its files are on screen; "files" those shown. Answers each
 * file with its picture where it has come, and whether it is still on its way.
 */
const useLockedPictures = ({open, files}) => {
  const client = useApolloClient()
  const [{pictures, settled}, setKnown] = useState(NOTHING)
  const asked = useRef(new Set())
  // Moves on each time Locked seals, so an answer to an ask made before then is let go.
  const generation = useRef(0)
  const missing = open ? files.filter(file => wantsPicture(file) && !asked.current.has(file.id)).map(({id}) => id) : []

  useEffect(() => {
    if (open) return

    generation.current += 1
    asked.current = new Set()
    setKnown(NOTHING)
  }, [open])

  useEffect(() => {
    if (!missing.length) return

    const askedIn = generation.current

    batchesOf(missing).forEach(ids => {
      ids.forEach(id => asked.current.add(id))

      client.query({query: LOCKED_THUMBNAILS, variables: {uploadIds: ids}, fetchPolicy: 'no-cache'})
        .then(({data}) => data.lockedThumbnails.map(({uploadId, url}) => [uploadId, url]))
        // A tile with no picture keeps its glyph rather than shimmering for good.
        .catch(() => [])
        .then(found => {
          if (askedIn !== generation.current) return

          setKnown(known => ({
            pictures: new Map([...known.pictures, ...found]),
            settled: new Set([...known.settled, ...ids])
          }))
        })
    })
  }, [missing.join()])

  return {
    withPicture: file => (pictures.has(file.id) ? {...file, thumbnail: {...file.thumbnail, url: pictures.get(file.id)}} : file),
    pending: file => open && wantsPicture(file) && !settled.has(file.id)
  }
}

export default useLockedPictures
